import classNames from 'classnames';
import React from 'react';

import {BackendSourceOptions, discoveryTracker} from '@udemy/browse-event-tracking';
import {useI18n} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Button} from '@udemy/react-core-components';

import {CertCardVertical} from './cert-card.react-component';
import styles from './cert-prep-module.module.less';

export interface CertCard {
    title: string;
    cardMediaProps: {
        src: string;
    };
    description: string;
    num_enrollments?: number;
    href: string;
}

interface CertPrepModuleProps {
    /**
     * The heading element to use for the title.
     * @defaultValue 'h2'
     */
    headingElement?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
    /**
     * The title of the module.
     * @defaultValue 'Get certified and get ahead in your career'
     */
    title?: string;
    /**
     * The subtitle of the module.
     * @defaultValue 'Prep for industry-recognized certifications with comprehensive courses, practice tests, and special offers on exam vouchers for select certifications.'
     */
    subTitle?: string;
    /**
     * The URL of the CTA button.
     * @defaultValue '/certifications/'
     */
    ctaUrl?: string;
    /**
     * The callback to call when the CTA button is clicked.
     */
    onClickCta?: () => void;
    /**
     * The optionalclass name to apply to the component.
     */
    className?: string;
}

/**
 * Certification Preparation Module component
 * Displays certification course cards with call-to-action
 */
export const CertPrepModule = ({
    className = '',
    headingElement = 'h2',
    title,
    subTitle,
    ctaUrl = '/browse/certification/',
    onClickCta,
}: CertPrepModuleProps) => {
    const {gettext} = useI18n();
    const HeadingElement = headingElement;
    const headingText = title || gettext('Get certified and get ahead in your career');
    const subTitleText =
        subTitle ||
        gettext(
            'Prep for certifications with comprehensive courses, practice tests, and special offers on exam vouchers.',
        );

    // Enrollment numbers have been commented out for now
    // as the translations are not being formatted correctly
    const CERT_CARDS: CertCard[] = [
        {
            title: 'CompTIA',
            cardMediaProps: {
                src: 'https://frontends.udemycdn.com/frontends-homepage/staticx/udemy/images/cert-prep/comptia-thumbnail.png',
            },
            description: gettext('Cloud, Networking, Cybersecurity'),
            // num_enrollments: 3000001,
            href: '/browse/certification/comptia-certifications/',
        },
        {
            title: 'AWS',
            cardMediaProps: {
                src: 'https://frontends.udemycdn.com/frontends-homepage/staticx/udemy/images/cert-prep/aws-thumbnail.png',
            },
            description: gettext('Cloud, AI, Coding, Networking'),
            // num_enrollments: 8000001,
            href: '/browse/certification/aws-certifications/',
        },
        {
            title: 'PMI',
            cardMediaProps: {
                src: 'https://frontends.udemycdn.com/frontends-homepage/staticx/udemy/images/cert-prep/pmi-thumbnail.png',
            },
            description: gettext('Project & Program Management'),
            // num_enrollments: 3000001,
            href: '/browse/certification/project-management-institute-pmi-certifications/',
        },
    ];

    const onClick = (certCard: CertCard, index: number) => {
        discoveryTracker.trackDiscoveryItemClickEvent(
            {
                item: {
                    id: index,
                    title: certCard.title,
                    type: 'course_topic',
                },
            },
            {
                backendSource: 'discovery' as BackendSourceOptions,
                index: index,
                uiRegion: 'certs',
            },
        );
    };

    const onImpression = (certCard: CertCard, index: number) => {
        discoveryTracker.trackDiscoveryImpression(
            {
                item: {
                    id: index,
                    title: certCard.title,
                    type: 'course_topic',
                },
            },
            {
                backendSource: 'discovery' as BackendSourceOptions,
                index: index,
                uiRegion: 'certs',
            },
        );
    };

    return (
        <section
            className={classNames(styles['cert-prep-module'], className)}
            data-testid="cert-prep-module"
        >
            <div className={styles['text-section']}>
                <div className={styles['header-content']}>
                    <HeadingElement className={classNames('ud-heading-xxl', styles.title)}>
                        {headingText}
                    </HeadingElement>
                    <p className={classNames('ud-text-md', styles.description)}>{subTitleText}</p>
                </div>

                <Button
                    componentClass="a"
                    href={ctaUrl}
                    size="large"
                    udStyle="ghost"
                    className="ud-link-white"
                    onClick={onClickCta}
                >
                    {gettext('Explore certifications and vouchers')}{' '}
                    <Icon label={false} glyph="arrow-right" className="ud-icon-bi-directional" />
                </Button>
            </div>
            <div className={styles['cards-section']}>
                {CERT_CARDS.map((card, i) => (
                    <CertCardVertical
                        key={card.title}
                        title={card.title}
                        cardMediaProps={card.cardMediaProps}
                        description={card.description}
                        num_enrollments={card.num_enrollments}
                        href={card.href}
                        onClick={() => onClick(card, i)}
                        onImpression={() => onImpression(card, i)}
                    />
                ))}
            </div>
        </section>
    );
};
